import React from "react";
import { v4 as uuid } from "uuid";
import CollapseCard from "./ui/CollapseCard";
import BusinessHours from "./ui/BusinessHours";
import { MODE_LABELS } from "../ReservationConfigForm";
import { err, clsErr } from "./validation";            // ⬅️ NEW
import Required from "../utils/Required";

export default function OperationalHoursForm({
  value = [],
  modeData = {},
  onChange,
  errors = {},
}) {
  const leadDays = Number.isInteger(modeData?.booking_lead_days) ? modeData.booking_lead_days : 0;
  const maxExceptions = Math.min(50, leadDays || 0);
  /* offerings grouped by mode – unchanged */
  const modeItems = {
    personal_services: (modeData.personal_services?.services ?? []).map((o) => ({
      id: o.id,
      name: o.name,
    })),
    table_inventory: (modeData.table_inventory?.services ?? []).map((service) => ({
      id: service.id,
      name: service.name || "Dining service",
    })),
    class_based: (modeData.class_based?.classes ?? []).map((o) => ({
      id: o.id,
      name: o.title,
    })),
    accommodation: (modeData.accommodation?.room_types ?? []).map((r, idx) => ({
      id: r.id ?? `room_${idx}`,
      name: r.name,
    })),
  };

  /* flags & CRUD helpers – unchanged */
  const hasAllRule = value.some((r) => r.scope === "all");
  const maxRules = 10;

  const add = () =>
    !hasAllRule &&
    onChange([
      ...value,
      { id: uuid(), label: "", scope: "all", applies_to: {}, hours: {} },
    ]);

  const set = (id, patch) =>
    onChange(value.map((h) => (h.id === id ? { ...h, ...patch } : h)));

  const remove = (id) => onChange(value.filter((h) => h.id !== id));

  const takenElsewhere = (rowId, modeKey, offerId) =>
    value.some(
      (r) => r.id !== rowId && r.applies_to?.[modeKey]?.includes(offerId)
    );

  /* ─────────────────── UI ──────────────────────────────────────── */
  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500">Exceptions per rule are limited to {maxExceptions} (min of 50 and booking lead days).</p>
      <div className="flex items-center gap-3">
        <button
          onClick={add}
          disabled={hasAllRule || (value?.length || 0) >= maxRules}
          className="text-blue-600 text-sm hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
        >
          + Add hours rule
        </button>
        <span className="text-xs text-gray-500">{(value?.length || 0)}/{maxRules}</span>
      </div>

      {/* top-level errors */}
      {err(errors, "operational_hours_rules") && (
        <p className="text-xs text-red-500">
          {Array.isArray(err(errors, "operational_hours_rules"))
            ? err(errors, "operational_hours_rules").join("; ")
            : err(errors, "operational_hours_rules")}
        </p>
      )}

      {hasAllRule && value.length > 1 && (
        <p className="text-xs text-red-500">
          One rule already covers <b>all offerings</b>.
        </p>
      )}

      {value.map((r, idx) => {

        const base = `operational_hours_rules${idx}`;
        const copySources = value
            .filter((h) => h.id !== r.id)
            .map(({ id, label }) => ({ id, label }));
        /* gather nested errors for header summary */
        const nestedErrs = [];
        ["label", "scope", "hours"].forEach((k) => {
          const e = err(errors, `${base}.${k}`);
          if (e) nestedErrs.push(e);
        });

        return (
          <CollapseCard
            key={r.id}
            title={r.label || "Untitled"}
            right={
              value.length > 1 && (
                <button
                  onClick={() => remove(r.id)}
                  className="text-red-500 text-xs hover:underline"
                >
                  Remove
                </button>
              )
            }
            hasError={nestedErrs.length > 0}
          >
            {nestedErrs.length > 0 && (
              <p className="text-xs text-red-500 mb-2">
                {nestedErrs.flat().join("; ")}
              </p>
            )}

            {/* ───── header line ───── */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
              {/* label */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Label <Required /></label>
                <input
                  placeholder="Label"
                  value={r.label}
                  onChange={(e) => set(r.id, { label: e.target.value })}
                  className={`rounded border-gray-300 px-2 py-1 w-full ${clsErr(
                    errors,
                    `${base}.label`
                  )}`}
                  data-errkey={`${base}.label`}
                />
                {err(errors, `${base}.label`) && (
                  <p className="text-xs text-red-500">
                    {err(errors, `${base}.label`)}
                  </p>
                )}
              </div>

              {/* scope selector */}
              <div>
                <label className="text-xs text-gray-600 mb-1 block">Scope <Required /></label>
                <select
                  value={r.scope}
                  onChange={(e) => {
                    const s = e.target.value;
                    set(r.id, {
                      scope: s,
                      applies_to: s === "custom" ? r.applies_to : {},
                    });
                  }}
                  className={`rounded border-gray-300 px-2 py-1 w-full ${clsErr(
                    errors,
                    `${base}.scope`
                  )}`}
                >
                  <option value="all">All offerings</option>
                  <option value="custom">Custom selection</option>
                </select>
              </div>
            </div>

            {/* hours picker */}
            <BusinessHours
              value={r.hours}
              onChange={(hours) => set(r.id, { hours })}
              exceptionsLimit={maxExceptions}
              copySources={copySources}
              onCopyFrom={(srcId) => {
                const src = value.find((h) => h.id === srcId);
                if (!src) return;
                set(r.id, {
                  hours: {
                    ...r.hours,
                    exceptions: JSON.parse(
                      JSON.stringify(src.hours?.exceptions || [])
                    ),
                  },
                });
              }}
              errors={errors}
              basePath={`${base}.hours`}
            />

            {/* custom offering selector */}
            {r.scope === "custom" && (
              <div className="mt-4 space-y-6">
                {Object.entries(modeItems).map(([modeKey, items]) =>
                  items.length ? (
                    <div key={modeKey}>
                      <h4 className="font-semibold text-sm mb-1">
                        {MODE_LABELS[modeKey]}
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {items.map(({ id, name }, i2) => {
                          const sel = r.applies_to?.[modeKey]?.includes(id);
                          const disabled =
                            !sel && takenElsewhere(r.id, modeKey, id);
                          return (
                            <label
                              key={id}
                              className={`text-xs flex items-center gap-1 ${
                                disabled
                                  ? "opacity-40 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                disabled={disabled}
                                checked={sel}
                                onChange={() => {
                                  const list = r.applies_to?.[modeKey] ?? [];
                                  const next = sel
                                    ? list.filter((x) => x !== id)
                                    : [...list, id];
                                  set(r.id, {
                                    applies_to: {
                                      ...r.applies_to,
                                      [modeKey]: next,
                                    },
                                  });
                                }}
                              />
                              {name || `Item ${i2 + 1}`}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ) : null
                )}

                {err(errors, `${base}.scope`) && (
                  <p className="text-xs text-red-500">
                    {err(errors, `${base}.scope`)}
                  </p>
                )}
              </div>
            )}
          </CollapseCard>
        );
      })}
    </div>
  );
}
